/**
 * Billing Service — credits-first.
 *
 * After Wave B-2, /billing/info returns:
 *   {
 *     credits: { balanceMilliCents, balanceUsd, activeRates, limits, freeGrant },
 *     addons:  { dedicatedEgress: {...}, warmPool: {...} },
 *     stripeCustomerId,
 *     topupHistory: [...],
 *     recentLedger:  [...],
 *   }
 *
 * Subscription / planLimits / invoices fields have been removed. Legacy
 * helpers (formatSubscription, cancelSubscription, etc.) are kept as
 * thin no-ops only to avoid breaking any callers that haven't been
 * migrated yet; they all return null / throw a guarded error so a stale
 * call surfaces immediately in dev instead of silently corrupting state.
 */
import { accountApi as apiService } from './api';

class BillingService {
  /**
   * Fetch the credits-first billing snapshot. Returns the raw backend
   * shape; callers destructure the slice they need (credits, addons,
   * topupHistory, recentLedger).
   */
  async getBillingInfo() {
    try {
      const response = await apiService.request('/billing/info');
      return {
        credits: response.credits || null,
        addons: response.addons || {
          dedicatedEgress: { enabled: false },
          warmPool: { enabled: false, quantity: 0 },
        },
        stripeCustomerId: response.stripeCustomerId || null,
        topupHistory: Array.isArray(response.topupHistory) ? response.topupHistory : [],
        recentLedger: Array.isArray(response.recentLedger) ? response.recentLedger : [],
      };
    } catch (error) {
      console.error('Error fetching billing info:', error);
      // Surface a stable empty shape so the UI can render skeleton/empty
      // states without null-checks on every field.
      return {
        credits: null,
        addons: {
          dedicatedEgress: { enabled: false },
          warmPool: { enabled: false, quantity: 0 },
        },
        stripeCustomerId: null,
        topupHistory: [],
        recentLedger: [],
      };
    }
  }

  /**
   * Apply a Stripe coupon / promotion code. Coupons now flow through the
   * top-up Checkout session, not a subscription, but the endpoint shape
   * is unchanged on the server.
   */
  async applyCoupon(code) {
    const response = await apiService.request('/billing/coupon/apply', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return response;
  }
}

const billingService = new BillingService();
export default billingService;
